import { useQuery } from "@tanstack/react-query"

import { apiFetch } from "@/api/client"
import type { paths } from "@/api/schema"

/**
 * Hooks for `backend/app_core/routers/sync.py`. Currently only the
 * admin-gated `/api/sync/logs` reader (T3.11). Mirrors the conventions
 * documented in `frontend/src/api/README.md`:
 *
 *   1) Derive the response wrapper type from `paths` so the path string
 *      stays type-checked against the generated OpenAPI schema.
 *   2) Query key starts with the router name ("sync") so cache
 *      invalidation can scope by router.
 *   3) `useQuery` around `apiFetch<Response>(path)`.
 *
 * Deviation note: the FastAPI handler doesn't declare a `response_model`
 * (it returns a manually-built `JSONResponse`), so the generated schema
 * type for the JSON body is `unknown`. We therefore declare an explicit
 * `SyncLogRow` shape mirroring `sync.py:459-470`. When that handler grows
 * a Pydantic model, swap this for the generated row type.
 */

export interface SyncLogRow {
  id: number
  job_type: string | null
  status: string
  started_at: string | null
  completed_at: string | null
  duration_seconds: number | null
  details: string | null
}

export interface SyncLogsResponse {
  success: boolean
  message?: string
  data?: SyncLogRow[]
}

// Compile-time guard: the path stays in the generated schema. If T1.1's
// rename ever flips back, this fails to typecheck and we know to update
// the call below.
type _SyncLogsPath = paths["/api/sync/logs"]["get"]
void (null as unknown as _SyncLogsPath)

export const SYNC_QUERY_KEYS = {
  all: ["sync"] as const,
  logs: (limit: number) => ["sync", "logs", limit] as const,
} as const

export function useSyncLogs(limit = 50) {
  return useQuery<SyncLogsResponse>({
    queryKey: SYNC_QUERY_KEYS.logs(limit),
    queryFn: () =>
      apiFetch<SyncLogsResponse>(`/api/sync/logs?limit=${limit}`),
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
    retry: false,
  })
}
